import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Mailbox,
  Inbox,
  AlertTriangle,
  Paperclip,
  ShieldAlert,
} from "lucide-react";

const POLL_MS = 60_000; // refresh every minute
const HOUR_MS = 60 * 60 * 1000;

const statusMeta = (status) => {
  switch (status) {
    case "relayed":
      return { label: "Relayed", tone: "emerald" };
    case "relay_failed":
      return { label: "Relay failed", tone: "red" };
    case "unknown_slug":
      return { label: "Unknown slug", tone: "zinc" };
    case "unknown_prefix":
      return { label: "Unknown prefix", tone: "zinc" };
    case "slug_recently_changed":
      return { label: "Recently renamed", tone: "amber" };
    case "no_recipients":
      return { label: "No recipients", tone: "amber" };
    case "rejected_signature":
      return { label: "Bad signature", tone: "red" };
    default:
      return { label: status || "—", tone: "zinc" };
  }
};

const toneClass = {
  emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  red: "bg-red-500/10 text-red-300 border-red-500/30",
  amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  zinc: "bg-white/5 text-muted-foreground border-white/10",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < HOUR_MS) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 24 * HOUR_MS) return `${Math.floor(diff / HOUR_MS)}h ago`;
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const InboundMailCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/inbound/audit", { params: { limit: 20 } });
      setRows(res.data || []);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load inbound audit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    let pollId = null;

    const startPolling = () => {
      if (pollId) return;
      pollId = setInterval(load, POLL_MS);
    };
    const stopPolling = () => {
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
    };

    // Only poll while the tab is visible — saves battery on mobile and
    // avoids piling up stale requests when the admin goes AFK.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        load();
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    if (document.visibilityState === "visible") startPolling();

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [load]);

  const hasFresh = rows.some(
    (r) => r.received_at && Date.now() - new Date(r.received_at).getTime() < HOUR_MS
  );

  return (
    <Card
      className="glass-card border-amber-500/20"
      data-testid="inbound-mail-card"
    >
      <CardHeader className="border-b border-white/5">
        <CardTitle className="font-heading flex items-center gap-2">
          <div className="relative">
            <Mailbox className="w-5 h-5 text-amber-400" />
            {hasFresh && (
              <span
                className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse"
                title="New inbound mail in the last hour"
                data-testid="inbound-fresh-dot"
              />
            )}
          </div>
          Inbound mail
          <Badge variant="secondary" className="ml-auto bg-white/5 text-muted-foreground border-white/10 text-[10px]">
            Last 20
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : err ? (
          <div className="p-6 text-sm text-red-400 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> {err}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="inbound-empty">
            <Inbox className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No inbound emails yet. Once someone emails your band, it'll
              show up here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="inbound-audit-list">
            {rows.map((r) => {
              const s = statusMeta(r.status);
              const isFresh =
                r.received_at && Date.now() - new Date(r.received_at).getTime() < HOUR_MS;
              return (
                <li
                  key={r.id || `${r.received_at}-${r.recipient}`}
                  className="px-5 py-3 hover:bg-white/[0.02] transition"
                  data-testid="inbound-audit-row"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground truncate max-w-xs">
                          {r.subject || <em className="text-muted-foreground">(no subject)</em>}
                        </span>
                        <Badge
                          className={`${toneClass[s.tone]} text-[10px] border`}
                          variant="secondary"
                        >
                          {s.label}
                        </Badge>
                        {isFresh && (
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
                            title="Within the last hour"
                          />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        <span className="text-foreground/70">From</span>{" "}
                        <span className="font-mono">{r.sender || r.from_header || "—"}</span>{" "}
                        <span className="text-foreground/70">to</span>{" "}
                        <span className="font-mono">
                          {r.prefix && r.prefix !== "(bare)" ? `${r.prefix}.` : ""}
                          {r.slug || "?"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span data-testid="audit-row-time">{fmtTime(r.received_at)}</span>
                        {r.status === "relayed" && (
                          <span className="text-emerald-400/80">
                            Delivered to {r.recipient_count}{" "}
                            {r.recipient_count === 1 ? "person" : "people"}
                          </span>
                        )}
                        {r.status === "relay_failed" && (
                          <span className="text-red-400/80 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {r.relay_failed || 0} / {r.recipient_count || 0} failed
                          </span>
                        )}
                        {r.attachment_count > 0 && (
                          <span className="flex items-center gap-1 text-foreground/70">
                            <Paperclip className="w-3 h-3" />
                            {r.attachment_count} attachment
                            {r.attachment_count === 1 ? "" : "s"}
                            {r.attachments_skipped > 0 && (
                              <span className="text-amber-300">
                                {" "}
                                ({r.attachments_skipped} skipped)
                              </span>
                            )}
                          </span>
                        )}
                        {r.reason && (
                          <span className="text-amber-300/80 italic">{r.reason}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default InboundMailCard;
